import { ContentMedia, TextAlignment } from '@/client/courses';
import { ContentVideo } from '@/ui/content-video';
import { EditableText } from '@/ui/editable-text/EditableText';
import { FlexContainer } from '@/ui/styled-ui';
import { isVideoFile } from '@/utils/helpers/content-media.helper';
import { Image } from 'primereact/image';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const toFlexAlignment = (textAlignment?: TextAlignment) =>
  textAlignment === TextAlignment.CENTER
    ? 'center'
    : textAlignment === TextAlignment.RIGHT
    ? 'flex-end'
    : 'flex-start';

const toObjectPosition = (textAlignment?: TextAlignment) =>
  textAlignment === TextAlignment.CENTER
    ? 'center'
    : textAlignment === TextAlignment.RIGHT
    ? 'right'
    : 'left';

const StyledMediaItem = styled.div<{ $textAlignment?: TextAlignment }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $textAlignment }) => toFlexAlignment($textAlignment)};
  justify-content: flex-start;
  gap: var(--small-padding);
  width: 100%;
  min-width: 0;
`;

const StyledMediaFrame = styled(FlexContainer)<{
  $withBorder: boolean;
  $textAlignment?: TextAlignment;
  $mediaHeight?: string;
}>`
  flex: ${({ $mediaHeight }) => ($mediaHeight ? '0 0 auto' : '1')};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: ${({ $textAlignment }) => toFlexAlignment($textAlignment)};
  flex-shrink: 0;
  position: relative;
  padding: 0;
  border: none !important;
  background: transparent;

  ${({ $mediaHeight }) =>
    $mediaHeight
      ? css`
          height: ${$mediaHeight};
          max-height: ${$mediaHeight};
        `
      : css`
          height: auto;
          max-height: none;
        `}

  .p-image {
    display: inline-flex !important;
    align-items: center;
    justify-content: ${({ $textAlignment }) => toFlexAlignment($textAlignment)};
    width: auto !important;
    height: auto !important;
    max-width: 100%;
    max-height: ${({ $mediaHeight }) => $mediaHeight ?? 'none'};
    border: none !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .p-image img {
    border-radius: var(--medium-border-radius);
    border: ${({ $withBorder }) =>
      $withBorder ? '4px solid var(--gray-moderate)' : 'none'} !important;
    width: auto !important;
    max-width: 100%;
    height: auto !important;
    max-height: ${({ $mediaHeight }) => $mediaHeight ?? 'none'};
    object-fit: contain;
    object-position: ${({ $textAlignment }) =>
      toObjectPosition($textAlignment)};
    display: block;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  video {
    border-radius: var(--medium-border-radius);
    object-fit: contain;
  }

  button {
    border-radius: var(--medium-border-radius);
  }
`;

const StyledCaption = styled(FlexContainer)<{ $textAlignment?: TextAlignment }>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 0 1 auto;

  h3,
  p {
    text-align: ${({ $textAlignment }) => $textAlignment ?? 'start'};
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    color: var(--black-main);
    margin: 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  h3 {
    font-weight: 700;
  }

  p {
    font-weight: 400;
  }
`;

const StyledEditableField = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

type ContentMediaItemProps<T extends ContentMedia> = {
  media: T;
  onChange?: (media: T) => void;
  textAlignment?: TextAlignment;
  mediaHeight?: string;
  alt?: string;
};

export const ContentMediaItem = <T extends ContentMedia>({
  media,
  onChange,
  textAlignment,
  mediaHeight,
  alt = '',
}: ContentMediaItemProps<T>) => {
  const { t } = useTranslation();
  const placeholder = t('content.media.text');
  const toCaptionText = (value: string) =>
    value === placeholder ? undefined : value;
  const isVideo = isVideoFile(media.file);

  return (
    <StyledMediaItem $textAlignment={textAlignment}>
      {media.file?.signedUrl && (
        <StyledMediaFrame
          $withBorder={!isVideo && !!media.withBorder}
          $textAlignment={textAlignment}
          $mediaHeight={mediaHeight}
        >
          {isVideo ? (
            <ContentVideo
              src={media.file.signedUrl}
              withControls={media.withControls}
            />
          ) : (
            <Image
              src={media.file.signedUrl}
              alt={alt}
              preview={!!media.withZoom}
              loading="lazy"
            />
          )}
        </StyledMediaFrame>
      )}
      <StyledCaption
        direction="column"
        align="stretch"
        justify="flex-start"
        $textAlignment={textAlignment}
      >
        {onChange ? (
          <>
            <StyledEditableField>
              <EditableText
                multiline
                isBold
                textAlignment={textAlignment}
                value={media.title || placeholder}
                saveValue={(value: string) =>
                  onChange({ ...media, title: toCaptionText(value) })
                }
              />
            </StyledEditableField>
            <StyledEditableField>
              <EditableText
                multiline
                textAlignment={textAlignment}
                value={media.description || placeholder}
                saveValue={(value: string) =>
                  onChange({ ...media, description: toCaptionText(value) })
                }
              />
            </StyledEditableField>
          </>
        ) : (
          <>
            {media.title && <h3>{media.title}</h3>}
            {media.description && <p>{media.description}</p>}
          </>
        )}
      </StyledCaption>
    </StyledMediaItem>
  );
};
